import { type ColumnDef } from '@tanstack/react-table'
import { Edit, Eye } from 'lucide-react'

import { SortableColumnHeader } from '@/components/filters/sortable-column-header'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import type { ListSelection } from '@/lib/list-selection'
import { PersonIdentityCell } from '@/components/person-identity-cell'
import type { AdminUser } from '@/hooks/use-admin-users'
import {
  AdminUserAccessBadges,
  AdminUserGroupBadges,
  formatAdminUserDate,
  formatAdminUserLastLogin,
  adminUserToPersonModel,
} from '@/lib/admin-user-display'
import {
  isAdminUsersColumnSort,
  adminUsersSortDirectionForColumn,
  toggleAdminUsersColumnSort,
  type AdminUsersSort,
  type AdminUsersSortColumn,
} from '@/lib/list-filters/admin-users'

type AdminUsersColumnHandlers = {
  onView: (userId: string) => void
  onEdit: (userId: string, user: AdminUser) => void
  sortBy: AdminUsersSort
  onSortChange: (sort: AdminUsersSort) => void
  selection?: ListSelection
  visibleIds?: string[]
}

function selectColumn(selection: ListSelection, visibleIds: string[]): ColumnDef<AdminUser> {
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selection.selected.has(id))
  return {
    id: 'select',
    header: () => (
      <Checkbox
        checked={allSelected}
        onCheckedChange={(checked) => selection.onToggleAll(visibleIds, checked === true)}
        aria-label="Select all users"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={selection.selected.has(row.original.id)}
        onClick={(e) => e.stopPropagation()}
        onCheckedChange={() => selection.onToggle(row.original.id)}
        aria-label={`Select ${row.original.email}`}
      />
    ),
  }
}

function sortableHeader(
  label: string,
  column: AdminUsersSortColumn,
  sortBy: AdminUsersSort,
  onSortChange: (sort: AdminUsersSort) => void,
) {
  return (
    <SortableColumnHeader
      label={label}
      active={isAdminUsersColumnSort(sortBy, column)}
      direction={adminUsersSortDirectionForColumn(sortBy, column)}
      onToggle={() => onSortChange(toggleAdminUsersColumnSort(sortBy, column))}
    />
  )
}

export function getAdminUsersColumns({
  onView,
  onEdit,
  sortBy,
  onSortChange,
  selection,
  visibleIds = [],
}: AdminUsersColumnHandlers): ColumnDef<AdminUser>[] {
  const leading = selection ? [selectColumn(selection, visibleIds)] : []
  return [
    ...leading,
    {
      id: 'user',
      header: () => sortableHeader('User', 'user', sortBy, onSortChange),
      cell: ({ row }) => {
        const person = adminUserToPersonModel(row.original)
        return (
          <PersonIdentityCell
            className="min-w-[12rem]"
            size="sm"
            name={person.name}
            subline={person.subtitle}
            colorSeed={person.colorSeed}
          />
        )
      },
    },
    {
      id: 'status',
      header: () => sortableHeader('Status', 'status', sortBy, onSortChange),
      cell: ({ row }) => <AdminUserAccessBadges user={row.original} />,
    },
    {
      id: 'groups',
      header: () => sortableHeader('Groups', 'groups', sortBy, onSortChange),
      cell: ({ row }) => <AdminUserGroupBadges groups={row.original.groups} />,
    },
    {
      id: 'joined',
      header: () => sortableHeader('Joined', 'joined', sortBy, onSortChange),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {formatAdminUserDate(row.original.date_joined)}
        </span>
      ),
    },
    {
      id: 'last_login',
      header: () => sortableHeader('Last login', 'last_login', sortBy, onSortChange),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {formatAdminUserLastLogin(row.original.last_login)}
        </span>
      ),
    },
    {
      id: 'actions',
      size: 88,
      minSize: 88,
      maxSize: 88,
      enablePinning: true,
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const user = row.original
        return (
          <div className="flex justify-end gap-1">
            <Button
              type="button"
              variant="ghostMuted"
              size="iconSm"
              className="shrink-0"
              aria-label="View user"
              title="View"
              onClick={(event) => {
                event.stopPropagation()
                onView(user.id)
              }}
            >
              <Eye className="size-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghostMuted"
              size="iconSm"
              className="shrink-0"
              aria-label="Edit user"
              title="Edit"
              onClick={(event) => {
                event.stopPropagation()
                onEdit(user.id, user)
              }}
            >
              <Edit className="size-4" aria-hidden />
            </Button>
          </div>
        )
      },
    },
  ]
}
